// incidentsApi — typed fetchers for /v1/incidents/* (Route 4).
import { apiFetch } from "../../lib/api-client.js";

export type ApiSeverity = "info" | "low" | "med" | "high" | "critical";
export type IncidentStatus = "open" | "ack" | "resolved";

export interface IncidentRow {
  id: string;
  tenant_id: string;
  site_id: string;
  site_name: string | null;
  reporter_officer_id: string | null;
  severity: ApiSeverity;
  status: IncidentStatus;
  title: string;
  body: string;
  created_at: string;
  resolved_at: string | null;
}

export interface IncidentListResponse {
  items: IncidentRow[];
  total: number;
  correlation_id: string | null;
}

export class IncidentsApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

export function friendlyIncidentsError(err: unknown): string {
  if (err instanceof IncidentsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色看不到事件清單。";
    if (err.status === 400 && err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
    if (err.status === 400 && err.code === "invalid_range")
      return "起訖日期不對，請確認結束日期晚於開始日期。";
    return "事件清單暫時無法載入。";
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<IncidentsApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
  } catch {}
  return new IncidentsApiError(r.status, code, cid);
}

export interface IncidentsQuery {
  tenantId?: string | null;
  siteId?: string | null;
  severity?: ApiSeverity | "" | null;
  status?: IncidentStatus | "" | null;
  from?: string | null;
  to?: string | null;
  limit?: number | null;
}

export async function listIncidents(params: IncidentsQuery = {}): Promise<IncidentListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.siteId) qs.set("site_id", params.siteId);
  if (params.severity) qs.set("severity", params.severity);
  if (params.status) qs.set("status", params.status);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.limit != null) qs.set("limit", String(params.limit));
  const r = await apiFetch(`/v1/incidents${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as IncidentListResponse;
}
